import {
  BodyText,
  Border,
  Container,
  GreenCircle,
  HistoryTableHeaderItem,
  HistoryTableHeaderWrapper,
  HistoryTableItem,
  HistoryTableItemWrapper,
  HistoryTableWrapper,
  Item,
  ItemWrapper,
  Link,
  List,
  ListItem,
  RedCircle,
  Section,
  Title,
} from '../List';
import { CONSENT_TYPE, DefaultUISettings, Service, TCFUISettings, UI_VARIANT } from '@usercentrics/cmp-browser-sdk';
import { FunctionalComponent, h } from 'preact';
import { memo, useState } from 'preact/compat';
import StorageInfo from '../StorageInfo';
import SubService from '../SubServices/SubServices';
import { isMailAddress } from '../../modules/helpers';
import { useEffect } from 'preact/hooks';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useUiContext } from '../../contexts/UiContext';

type Props = {
  onToggleExpandable?: () => void;
  onOpenSubServiceDetail?: (subService: Service) => void;
  service: Service;
};

const checkZero = (data: string): string => {
  let retData = data;

  if (retData.length === 1) {
    retData = '0'.concat(data);
  }

  return retData;
};

const dateFormatter = (date: Date): string => {
  let day = `${date.getDate()}`;
  let month = `${date.getMonth() + 1}`;
  let year = `${date.getFullYear()}`;
  let hour = `${date.getHours()}`;
  let minutes = `${date.getMinutes()}`;

  day = checkZero(day);
  month = checkZero(month);
  year = checkZero(year);
  hour = checkZero(hour);
  minutes = checkZero(minutes);

  return `${day}.${month}.${year}, ${hour}:${minutes}`;
};

const ExpandedListItemWithDefaults: FunctionalComponent<Props> = ({
  onOpenSubServiceDetail,
  onToggleExpandable,
  service,
}) => {
  const { uiVariant } = useUiContext();
  const { settings } = useSettingsContext();
  const {
    consent,
    cookieMaxAgeSeconds,
    cookieRefresh,
    dataCollected,
    dataProtectionOfficer,
    dataPurposes,
    dataRecipients,
    description,
    deviceStorage,
    deviceStorageDisclosureUrl,
    fetchSubServices,
    legalBasis,
    retentionPeriodDescription,
    technologiesUsed,
    usesCookies,
    usesNonCookieAccess,
  } = service;
  const subServiceSize = service.subServicesLength;
  const [activeElement, setActiveElement] = useState<EventTarget | null | undefined>(null);

  // For some reason, sometimes using the Tab key to navigate through links
  // does not work
  // This circunvents that issue and makes sure we have the element correctly focused
  useEffect(() => {
    if (activeElement) {
      (activeElement as HTMLElement).focus();
    }
  }, [activeElement]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleLinkFocus = (event: any) => {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (event.currentTarget) {
      setActiveElement(event.currentTarget);
    }
  };

  const { general, labels, serviceLabels } =
    uiVariant === UI_VARIANT.TCF
      ? {
          general: (settings.ui as TCFUISettings).labels.nonTCFLabels.general,
          labels: (settings.ui as TCFUISettings).labels,
          serviceLabels: (settings.ui as TCFUISettings).labels.nonTCFLabels.service,
        }
      : {
          general: (settings.ui as DefaultUISettings).labels.general,
          labels: (settings.ui as DefaultUISettings).labels,
          serviceLabels: (settings.ui as DefaultUISettings).labels.service,
        };
  const { cookieInformation: cookieInformationLabels } = labels;

  return (
    <Container>
      {description && (
        <Section>
          <Title>{serviceLabels.descriptionTitle}</Title>
          <BodyText noPadding>{description}</BodyText>
        </Section>
      )}

      {(service.processingCompany?.name || service.processingCompany?.address) && (
        <Section>
          <Title>{serviceLabels.processingCompanyTitle}</Title>
          <BodyText noPadding>
            {service.processingCompany.name}
            {service.processingCompany.name && <br />}
            {service.processingCompany.address}
          </BodyText>
        </Section>
      )}

      {dataProtectionOfficer && (
        <Section>
          <Title>{serviceLabels.dataProtectionOfficer.title}</Title>
          <BodyText>{serviceLabels.dataProtectionOfficer.description}</BodyText>
          <Link
            target="_blank"
            rel="noopener"
            onFocus={handleLinkFocus}
            href={isMailAddress(dataProtectionOfficer) ? `mailto:${dataProtectionOfficer}` : dataProtectionOfficer}
            tabIndex={0}
          >
            {dataProtectionOfficer}
          </Link>
        </Section>
      )}

      {dataPurposes.length > 0 && dataPurposes[0] !== '' && (
        <Section>
          <Title>{serviceLabels.dataPurposes.title}</Title>
          <BodyText>{serviceLabels.dataPurposes.description}</BodyText>
          <ItemWrapper>
            {dataPurposes.map((purpose: string) => (
              <Item>{purpose}</Item>
            ))}
          </ItemWrapper>
        </Section>
      )}

      {technologiesUsed.length > 0 && technologiesUsed[0] !== '' && (
        <Section>
          <Title>{serviceLabels.technologiesUsed.title}</Title>
          <BodyText>{serviceLabels.technologiesUsed.description}</BodyText>
          <ItemWrapper>
            {technologiesUsed.map((technologies: string) => (
              <Item>{technologies}</Item>
            ))}
          </ItemWrapper>
        </Section>
      )}

      {dataCollected.length > 0 && dataCollected[0] !== '' && (
        <Section>
          <Title>{serviceLabels.dataCollected.title}</Title>
          <BodyText>{serviceLabels.dataCollected.description}</BodyText>
          <ItemWrapper>
            {dataCollected.map((data: string) => (
              <Item>{data}</Item>
            ))}
          </ItemWrapper>
        </Section>
      )}

      {legalBasis.length > 0 && legalBasis[0] !== '' && (
        <Section>
          <Title>{serviceLabels.legalBasis.title}</Title>
          <BodyText>{serviceLabels.legalBasis.description}</BodyText>
          <ItemWrapper>
            {legalBasis.map((part: string) => (
              <Item>{part}</Item>
            ))}
          </ItemWrapper>
        </Section>
      )}

      {service.dataDistribution?.processingLocation && (
        <Section>
          <Title>{serviceLabels.dataDistribution.processingLocationTitle}</Title>
          <BodyText>{serviceLabels.dataDistribution.processingLocationDescription}</BodyText>
          <List>
            <ListItem showBulletPoints>
              <BodyText>{service.dataDistribution.processingLocation}</BodyText>
            </ListItem>
          </List>
        </Section>
      )}

      {retentionPeriodDescription && (
        <Section>
          <Title>{serviceLabels.retentionPeriod.title}</Title>
          <BodyText>{serviceLabels.retentionPeriod.description}</BodyText>
          <List>
            <ListItem showBulletPoints>
              <BodyText>{retentionPeriodDescription}</BodyText>
            </ListItem>
          </List>
        </Section>
      )}

      {service.dataDistribution?.thirdPartyCountries && (
        <Section>
          <Title>{serviceLabels.dataDistribution.thirdPartyCountriesTitle}</Title>
          <BodyText>{serviceLabels.dataDistribution.thirdPartyCountriesDescription}</BodyText>
          <ItemWrapper>
            <Item>{service.dataDistribution.thirdPartyCountries}</Item>
          </ItemWrapper>
        </Section>
      )}

      {dataRecipients.length > 0 && dataRecipients[0] !== '' && (
        <Section>
          <Title>{serviceLabels.dataRecipients.title}</Title>
          <BodyText>{serviceLabels.dataRecipients.description}</BodyText>
          <ItemWrapper>
            {dataRecipients.map((part: string) => (
              <Item>{part}</Item>
            ))}
          </ItemWrapper>
        </Section>
      )}

      {service.urls?.privacyPolicy && (
        <Section>
          <Title>{serviceLabels.urls.privacyPolicyTitle}</Title>
          <Link onFocus={handleLinkFocus} target="_blank" rel="noopener" href={service.urls.privacyPolicy} tabIndex={0}>
            {service.urls.privacyPolicy}
          </Link>
        </Section>
      )}

      {service.urls?.cookiePolicy && (
        <Section>
          <Title>{serviceLabels.urls.cookiePolicyTitle}</Title>
          <Link
            onFocus={handleLinkFocus}
            target="_blank"
            rel="noopener"
            data-testid="uc-settings-cookie-link"
            href={service.urls.cookiePolicy}
            tabIndex={0}
          >
            {service.urls.cookiePolicy}
          </Link>
        </Section>
      )}

      {service.urls?.optOut && (
        <Section>
          <Title>{serviceLabels.urls.optOutTitle}</Title>
          <Link
            onFocus={handleLinkFocus}
            target="_blank"
            rel="noopener"
            data-testid="uc-settings-output-link"
            href={service.urls.optOut}
            tabIndex={0}
          >
            {service.urls.optOut}
          </Link>
        </Section>
      )}

      <StorageInfo
        cookieInformationLabels={cookieInformationLabels}
        cookieMaxAgeSeconds={cookieMaxAgeSeconds}
        cookieRefresh={cookieRefresh}
        deviceStorage={deviceStorage}
        deviceStorageDisclosureUrl={deviceStorageDisclosureUrl}
        storageInfoAnchorLabel={`${service.name} ${settings.ui.ariaLabels.vendorDetailedStorageInformation}`}
        onToggleExpandable={onToggleExpandable}
        isNonIabVendor
        usesCookies={usesCookies}
        usesNonCookieAccess={usesNonCookieAccess}
      />

      {subServiceSize > 0 && onOpenSubServiceDetail ? (
        <SubService
          fetchSubServices={fetchSubServices}
          onOpenSubServiceDetail={onOpenSubServiceDetail}
          onToggleExpandable={onToggleExpandable}
          size={subServiceSize}
        />
      ) : (
        <Border />
      )}

      {consent.history.length > 0 && (
        <Section>
          <Title>{serviceLabels.history.title}</Title>
          <BodyText>{serviceLabels.history.description}</BodyText>

          <HistoryTableWrapper>
            <HistoryTableHeaderWrapper>
              <HistoryTableHeaderItem>{general.decision}</HistoryTableHeaderItem>
              <HistoryTableHeaderItem>{general.date}</HistoryTableHeaderItem>
            </HistoryTableHeaderWrapper>

            {consent.history.map((history) => {
              let decisionText = '';
              switch (history.type) {
                case CONSENT_TYPE.EXPLICIT:
                  decisionText = history.status ? general.consentGiven : general.consentNotGiven;
                  break;
                case CONSENT_TYPE.IMPLICIT:
                  decisionText = history.status ? general.implicitYes : general.implicitNo;
                  break;
                default:
                  break;
              }

              return (
                <HistoryTableItemWrapper>
                  <HistoryTableItem>
                    {history.status ? <GreenCircle /> : <RedCircle />}
                    {decisionText}
                  </HistoryTableItem>
                  <HistoryTableItem>{dateFormatter(new Date(history.timestamp))}</HistoryTableItem>
                </HistoryTableItemWrapper>
              );
            })}
          </HistoryTableWrapper>
        </Section>
      )}
    </Container>
  );
};

export default memo(ExpandedListItemWithDefaults);
